import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import Layout from '../../components/layout/Layout';
import { Card, CardContent } from '../../components/ui/card';

const AboutPage = () => {
  const { t, lang, isRTL } = useLanguage();

  const content = {
    ro: {
      history: `Moscheea Ar-Rahman din București este unul dintre cele mai importante centre islamice din România. Fondată pentru a servi comunitatea musulmană în creștere din capitala românească, moscheea oferă un loc de rugăciune, educație și întâlnire pentru musulmanii din întreaga țară.

De-a lungul anilor, moscheea a devenit un punct de referință pentru comunitatea musulmană din România, oferind servicii religioase, cursuri de limbă arabă, educație islamică pentru copii și adulți, precum și asistență socială pentru cei în nevoie.`,
      mission: `Misiunea noastră este de a promova valorile islamice de pace, compasiune și comunitate în societatea românească. Ne dedicăm:

• Furnizării unui spațiu sacru pentru rugăciune și reflecție
• Educației islamice pentru toate vârstele
• Construirii de punți între comunitatea musulmană și societatea românească
• Oferirea de asistență celor aflați în dificultate
• Promovarea dialogului interreligios și înțelegerii mutuale`,
      imam: `Imamul nostru conduce comunitatea cu înțelepciune și devotament. Cu studii aprofundate în științele islamice și o experiență vastă în leadership comunitar, imamul nostru ghidează credincioșii în practicarea corectă a islamului și în integrarea armonioasă în societatea românească.`,
    },
    en: {
      history: `The Ar-Rahman Mosque in Bucharest is one of the most important Islamic centers in Romania. Founded to serve the growing Muslim community in the Romanian capital, the mosque offers a place of prayer, education, and gathering for Muslims from across the country.

Over the years, the mosque has become a reference point for the Muslim community in Romania, offering religious services, Arabic language courses, Islamic education for children and adults, as well as social assistance for those in need.`,
      mission: `Our mission is to promote Islamic values of peace, compassion, and community in Romanian society. We are dedicated to:

• Providing a sacred space for prayer and reflection
• Islamic education for all ages
• Building bridges between the Muslim community and Romanian society
• Offering assistance to those in difficulty
• Promoting interfaith dialogue and mutual understanding`,
      imam: `Our imam leads the community with wisdom and devotion. With in-depth studies in Islamic sciences and vast experience in community leadership, our imam guides believers in the correct practice of Islam and harmonious integration into Romanian society.`,
    },
    ar: {
      history: `مسجد الرحمن في بوخارست هو أحد أهم المراكز الإسلامية في رومانيا. تأسس لخدمة المجتمع المسلم المتنامي في العاصمة الرومانية، يوفر المسجد مكانًا للصلاة والتعليم والاجتماع للمسلمين من جميع أنحاء البلاد.

على مر السنين، أصبح المسجد نقطة مرجعية للمجتمع المسلم في رومانيا، حيث يقدم الخدمات الدينية ودورات اللغة العربية والتعليم الإسلامي للأطفال والكبار، فضلاً عن المساعدة الاجتماعية للمحتاجين.`,
      mission: `مهمتنا هي تعزيز القيم الإسلامية للسلام والرحمة والمجتمع في المجتمع الروماني. نحن ملتزمون بـ:

• توفير مكان مقدس للصلاة والتأمل
• التعليم الإسلامي لجميع الأعمار
• بناء جسور بين المجتمع المسلم والمجتمع الروماني
• تقديم المساعدة لمن هم في حاجة
• تعزيز الحوار بين الأديان والتفاهم المتبادل`,
      imam: `يقود إمامنا المجتمع بحكمة وإخلاص. مع دراسات معمقة في العلوم الإسلامية وخبرة واسعة في قيادة المجتمع، يرشد إمامنا المؤمنين في الممارسة الصحيحة للإسلام والاندماج المتناغم في المجتمع الروماني.`,
    },
  };

  const localContent = content[lang] || content.ro;

  return (
    <Layout>
      {/* Header */}
      <section className="bg-islamic-teal text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-serif text-3xl md:text-4xl font-bold mb-2">
            {t('about.title')}
          </h1>
          <p className="text-white/70">
            {lang === 'ar' ? 'مسجد الرحمن بوخارست' : 'Moscheea Ar-Rahman București'}
          </p>
        </div>
      </section>

      <section className="py-8 md:py-12 bg-islamic-pearl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* History */}
          <Card className="border-stone-200 shadow-md mb-8">
            <CardContent className="p-6 md:p-8">
              <h2 className="font-serif text-2xl font-semibold text-stone-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 rounded bg-islamic-teal/10 flex items-center justify-center">
                  <span className="font-arabic text-islamic-teal">١</span>
                </span>
                {t('about.history')}
              </h2>
              <div className={`text-stone-600 leading-relaxed whitespace-pre-line ${isRTL ? 'font-arabic text-right' : ''}`}>
                {localContent.history}
              </div>
            </CardContent>
          </Card>

          {/* Mission */}
          <Card className="border-stone-200 shadow-md mb-8">
            <CardContent className="p-6 md:p-8">
              <h2 className="font-serif text-2xl font-semibold text-stone-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 rounded bg-islamic-gold/10 flex items-center justify-center">
                  <span className="font-arabic text-islamic-gold">٢</span>
                </span>
                {t('about.mission')}
              </h2>
              <div className={`text-stone-600 leading-relaxed whitespace-pre-line ${isRTL ? 'font-arabic text-right' : ''}`}>
                {localContent.mission}
              </div>
            </CardContent>
          </Card>

          {/* Imam */}
          <Card className="border-stone-200 shadow-md">
            <CardContent className="p-6 md:p-8">
              <h2 className="font-serif text-2xl font-semibold text-stone-800 mb-4 flex items-center gap-3">
                <span className="w-8 h-8 rounded bg-islamic-teal/10 flex items-center justify-center">
                  <span className="font-arabic text-islamic-teal">٣</span>
                </span>
                {t('about.imam')}
              </h2>
              <div className={`text-stone-600 leading-relaxed whitespace-pre-line ${isRTL ? 'font-arabic text-right' : ''}`}>
                {localContent.imam}
              </div>
            </CardContent>
          </Card>

          {/* Arabic calligraphy */}
          <div className="mt-12 text-center">
            <p className="font-arabic text-3xl text-islamic-teal mb-2">بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ</p>
            <p className="text-stone-500 text-sm">
              {lang === 'ar' ? 'بسم الله الرحمن الرحيم' : lang === 'en' ? 'In the name of God, the Most Gracious, the Most Merciful' : 'În numele lui Dumnezeu, Cel Milostiv, Cel Îndurător'}
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
